import { useState, useMemo } from 'react';
import { isDay, todayIso, formatDay, monthOf, addMonths, monthGrid, monthName, lastDayOfMonth } from '../lib/dateFilter.mjs';

// ─── The calendar ─────────────────────────────────────────────────────────────
// A RANGE IN TWO TAPS: tap the start day, tap the end day. That is the whole
// control — the reason this exists is that a pair of <input type="date"> boxes
// is not a date range picker, it is two dates to TYPE, which is the user error
// the ticket was reopened for (Robyn Marais, ticket 726e700a).
//
// MOBILE-FIRST: one month at a time (two months side by side squeeze the cells
// below a thumb on a phone), every day ≥ 40px tall, nothing with a fixed width,
// and it renders INLINE inside the filter panel / bottom sheet — no popover to
// mis-stack or trap scroll on a phone.
//
// It holds NO date state of its own beyond the half-made selection: the range
// it reports is written straight back into the filter value by its parent, so
// there is one source of truth (the Looker expression).

const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

export default function DateRangeCalendar({ start, end, min, max, onPick }) {
  const today = todayIso();
  // Open on the month the user is already looking at: their start date, else
  // the end of the data (which is where recent dates live), else today.
  const [month, setMonth] = useState(() => monthOf(start || end || (max && max < today ? max : today)));
  const [anchor, setAnchor] = useState('');   // first tap, waiting for the second
  const [hover, setHover] = useState('');

  const cells = useMemo(() => monthGrid(month), [month]);
  const years = useMemo(() => {
    const lo = Number((min || today).slice(0, 4));
    const hi = Number((max && max > today ? max : today).slice(0, 4));
    const first = Math.min(lo, Number(month.slice(0, 4)));
    const last = Math.max(hi, Number(month.slice(0, 4)));
    return Array.from({ length: last - first + 1 }, (_, i) => first + i);
  }, [min, max, month, today]);

  // While a selection is half made the grid previews the range under the
  // cursor, so it is obvious that the second tap closes it.
  const previewEnd = anchor && hover ? hover : '';
  const lo = anchor ? (previewEnd && previewEnd < anchor ? previewEnd : anchor) : start;
  const hi = anchor ? (previewEnd && previewEnd < anchor ? anchor : previewEnd) : end;

  const blocked = (d) => (min && d < min) || (max && d > max);
  const prevMonth = addMonths(month, -1);
  const nextMonth = addMonths(month, 1);
  const canPrev = !min || lastDayOfMonth(prevMonth) >= min;
  const canNext = !max || `${nextMonth}-01` <= max;

  function tap(day) {
    if (blocked(day)) return;
    if (!anchor) { setAnchor(day); setHover(''); return; }       // tap 1 — the start
    setAnchor(''); setHover('');
    onPick(day < anchor ? day : anchor, day < anchor ? anchor : day); // tap 2 — either direction
  }

  return (
    <div style={wrap}>
      <div style={head}>
        <button type="button" onClick={() => setMonth(prevMonth)} disabled={!canPrev} style={{ ...navBtn, opacity: canPrev ? 1 : 0.35 }} aria-label="Previous month">‹</button>
        <select value={month.slice(5)} onChange={(e) => setMonth(`${month.slice(0, 4)}-${e.target.value}`)} style={jump} aria-label="Month">
          {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map((mm) => (
            <option key={mm} value={mm}>{monthName(`2000-${mm}`).slice(0, 3)}</option>
          ))}
        </select>
        <select value={month.slice(0, 4)} onChange={(e) => setMonth(`${e.target.value}-${month.slice(5)}`)} style={jump} aria-label="Year">
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <button type="button" onClick={() => setMonth(nextMonth)} disabled={!canNext} style={{ ...navBtn, opacity: canNext ? 1 : 0.35 }} aria-label="Next month">›</button>
      </div>

      <div style={grid} role="grid" aria-label={monthName(month)}>
        {WEEKDAYS.map((d, i) => <span key={i} style={dow}>{d}</span>)}
        {cells.map((day, i) => {
          if (!day) return <span key={i} />;
          const off = blocked(day);
          const isStart = !!lo && day === lo;
          const isEnd = !!hi && day === hi;
          const inside = !!lo && !!hi && day > lo && day < hi;
          const edge = isStart || isEnd;
          return (
            <button
              key={i}
              type="button"
              disabled={off}
              onClick={() => tap(day)}
              onMouseEnter={() => anchor && setHover(day)}
              title={formatDay(day)}
              aria-pressed={edge || inside}
              style={{
                ...cell,
                ...(inside ? cellInside : null),
                ...(edge ? cellEdge : null),
                ...(day === today && !edge ? cellToday : null),
                ...(off ? cellOff : null),
              }}
            >
              {Number(day.slice(8))}
            </button>
          );
        })}
      </div>

      <div style={foot}>
        {anchor ? (
          <>
            <span style={footNote}>Start {formatDay(anchor)} — now tap the end date</span>
            <button type="button" style={footBtn} onClick={() => { const a = anchor; setAnchor(''); setHover(''); onPick(a, ''); }}>From this date on</button>
            <button type="button" style={footBtn} onClick={() => { setAnchor(''); setHover(''); }}>Cancel</button>
          </>
        ) : (
          <span style={footNote}>{isDay(start) && isDay(end) ? 'Tap a day to start a new range' : 'Tap the start date, then the end date'}</span>
        )}
      </div>
    </div>
  );
}

// Mobile-first: fills its column, never sets a width, cells sized by the grid.
const wrap = { display: 'flex', flexDirection: 'column', gap: 6, border: '1px solid var(--hairline)', borderRadius: 12, padding: 8, background: 'var(--card)', minWidth: 0 };
const head = { display: 'flex', alignItems: 'center', gap: 6 };
const navBtn = { minWidth: 40, minHeight: 40, border: '1px solid var(--hairline)', borderRadius: 10, background: 'var(--card)', color: 'var(--text)', fontSize: 18, fontWeight: 700, cursor: 'pointer', lineHeight: 1 };
const jump = { flex: '1 1 0', minWidth: 0, minHeight: 40, padding: '0 8px', border: '1px solid var(--hairline)', borderRadius: 10, background: 'var(--card)', color: 'var(--text)', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const grid = { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 };
const dow = { textAlign: 'center', fontSize: 10.5, fontWeight: 700, color: 'var(--muted)', padding: '4px 0' };
const cell = { minHeight: 40, padding: 0, border: '1px solid transparent', borderRadius: 10, background: 'transparent', color: 'var(--text)', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const cellInside = { background: 'rgba(var(--brand-rgb), 0.16)', borderRadius: 4 };
const cellEdge = { background: 'var(--brand)', color: '#fff' };
const cellToday = { borderColor: 'var(--brand)' };
const cellOff = { color: 'var(--muted-2)', opacity: 0.4, cursor: 'not-allowed' };
const foot = { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 };
const footNote = { flex: '1 1 auto', fontSize: 11.5, color: 'var(--muted)', minWidth: 0 };
const footBtn = { minHeight: 32, padding: '4px 8px', background: 'transparent', border: 'none', color: 'var(--brand)', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
